import React, { useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { ChevronDownIcon } from "@chakra-ui/icons";
import Pagination from "components/pagination";
import FundsTable from "./FundsTable";
import { FiArrowLeft, FiPlus, FiDollarSign, FiAlertCircle, FiEdit, FiTrash2 } from "react-icons/fi";
import UpdateBalanceModal from "components/modals/UpdateBalanceModal";
import { useSelector, useDispatch } from "react-redux";
import InvoiceDetails from "../../invoiceManagement/components/InvoiceDetails";
import { fetchAllFundedInvoices, fetchAdminInvoiceDetails, attachInvoicePayment, updateInvoiceStatus, addInvoiceCharges, updateInvoicePayment, deleteInvoicePayment, updateInvoiceCharge, deleteInvoiceCharge } from "features/invoices/actions";
import { useToastNotification } from "utils/toast-utils";
import StatusUpdateModal from 'components/modals/StatusUpdateModal';
import ChargesModal from 'components/modals/ChargesModal';
import DeletePaymentModal from 'components/modals/DeletePaymentModal';
import DeleteChargeModal from 'components/modals/DeleteChargeModal';

const invoicesColumns = [
  { Header: "System ID", accessor: "invoiceNumber" },
  { Header: "Invoice Number", accessor: "referenceNumber" },
  { Header: "Debtor Name", accessor: "debtorName" },
  { Header: "Amount", accessor: "amount" },
  { Header: "Created On", accessor: "createdAt" },
  { Header: "Status", accessor: "status" },
  { Header: "Expected Due Date", accessor: "expectedDueDate" },
  { Header: "Actions", accessor: "actions" },
];

const formatDate = (dateInput) => {
  if (!dateInput) return '-';
  try {
    // Accept both string and Date object
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-'; // Check if date is valid
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return '-';
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const transformPaymentsData = (payments) => {
  if (!payments || !Array.isArray(payments)) return [];
  
  return payments.map(payment => ({
    _id: payment._id,
    transactionId: payment.transactionId || 'N/A',
    paymentDate: formatDate(payment.paymentDate),
    collectedAmount: formatCurrency(Number(payment.collectedAmount)),
    remainingAmount: formatCurrency(Number(payment.remainingAmount)),
    notes: payment.notes || '-'
  }));
};

const transformChargesData = (charges) => {
  if (!charges || !Array.isArray(charges)) return [];
  return charges.map(charge => ({
    id: charge._id || 'N/A',
    amount: formatCurrency(charge.chargeAmount),
    date: formatDate(charge.chargeDate),
    notes: charge.notes || '-',
    type: charge.type || 'Regular'
  }));
};

const getStatusColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  if (!normalizedStatus) return "gray.500";

  switch (normalizedStatus) {
    case "PENDING":
      return "orange.500";
    case "UNDER REVIEW":
      return "purple.500";
    case "APPROVED":
      return "green.500";
    case "REJECTED":
      return "red.500";
    case "FUNDED":
      return "teal.500";
    case "INCOMPLETE":
      return "yellow.500";
    case "DELETED":
      return "gray.500";
    case "CLOSED":
      return "blue.500";
    default:
      return "gray.500";
  }
};

const getStatusBgColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  if (!normalizedStatus) return "gray.100";

  switch (normalizedStatus) {
    case "PENDING":
      return "orange.100";
    case "UNDER REVIEW":
      return "purple.100";
    case "APPROVED":
      return "green.100";
    case "REJECTED":
      return "red.100";
    case "FUNDED":
      return "teal.100";
    case "INCOMPLETE":
      return "yellow.100";
    case "DELETED":
      return "gray.100";
    case "CLOSED":
      return "blue.100";
    default:
      return "gray.100";
  }
};

const getStatusDisplayName = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'Approved';
    case 'UNDER REVIEW':
      return 'Under Review';
    case 'REJECTED':
      return 'Rejected';
    case 'FUNDED':
      return 'Funded';
    case 'INCOMPLETE':
      return 'Incomplete';
    case 'CLOSED':
      return 'Closed';
    case 'DELETED':
      return 'Deleted';
    case 'PENDING':
      return 'Pending';
    case 'DRAFT':
      return 'Draft';
    default:
      return status || 'N/A';
  }
};

const FundingDetails = ({ fund, onBack }) => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const { selectedInvoice } = useSelector((state) => state.invoices);
  const invoice = selectedInvoice || fund;

  const [invoicePage, setInvoicePage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const itemsPerPage = 7;
  const [isUpdateBalanceModalOpen, setIsUpdateBalanceModalOpen] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [isChargesModalOpen, setIsChargesModalOpen] = useState(false);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isDeletePaymentModalOpen, setIsDeletePaymentModalOpen] = useState(false);
  const [loadingPaymentAction, setLoadingPaymentAction] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isDeleteChargeModalOpen, setIsDeleteChargeModalOpen] = useState(false);
  const [loadingChargeAction, setLoadingChargeAction] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('Closed');

  // Define payment columns inside the component
  const paymentColumns = [
    { Header: "Transaction ID", accessor: "transactionId" },
    { Header: "Payment Date", accessor: "paymentDate" },
    { Header: "Collected Amount", accessor: "collectedAmount" },
    { Header: "Remaining Amount", accessor: "remainingAmount" },
    { Header: "Notes", accessor: "notes" },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <HStack spacing={2}>
          <IconButton
            icon={<FiEdit />}
            size="sm"
            variant="ghost"
            aria-label="Edit Payment"
            onClick={(e) => {
              e.stopPropagation();
              handleEditPayment(row);
            }}
          />
          <IconButton
            icon={<FiTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label="Delete Payment"
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePayment(row);
            }}
          />
        </HStack>
      ),
    }
  ];

  // Updated funding overview with actual data
  const fundingOverview = {
    fundedAmount: formatCurrency(invoice?.fundedAmount || 0),
    remainingBalance: formatCurrency(invoice?.remainingAmount || 0),
    paymentStatus: getStatusDisplayName(invoice?.status),
    debtor: invoice?.debtorName || "N/A",
    requestDate: invoice?.createdAt ? formatDate(invoice.createdAt) : "N/A",
    dueDate: invoice?.expectedDueDate ? formatDate(invoice.expectedDueDate) : "N/A",
  };

  // Updated amount details with actual data
  const amountDetails = {
    invoiceAmount: formatCurrency(invoice?.amount || 0),
    advanceAmount: formatCurrency(invoice?.fundedAmount || 0),
    escrowAmount: formatCurrency((invoice?.escrowAmount) || 0),
    paymentAmount: formatCurrency(invoice?.paymentAmount || 0),
  };

  // Transform invoice data for table
  const invoiceTableData = useMemo(() => {
    if (!invoice) return [];
    
    return [{
      referenceNumber: invoice.referenceNumber || 'N/A',
      invoiceNumber: invoice.invoiceNumber || 'N/A',
      debtorName: invoice.debtorName || 'N/A',
      amount: formatCurrency(invoice.amount || 0),
      createdAt: formatDate(invoice.createdAt),
      status: getStatusDisplayName(invoice.status),
      expectedDueDate: formatDate(invoice.expectedDueDate),
      actions: null // Add your action buttons here if needed
    }];
  }, [invoice]);

  // Pagination logic for invoices
  const pagedInvoices = useMemo(() => {
    const start = (invoicePage - 1) * itemsPerPage;
    return invoiceTableData.slice(start, start + itemsPerPage);
  }, [invoicePage, invoiceTableData]);

  // Pagination logic for payments
  const pagedPayments = useMemo(() => {
    const start = (paymentPage - 1) * itemsPerPage;
    return (invoice?.payments || []).slice(start, start + itemsPerPage);
  }, [paymentPage, invoice?.payments]);

  // Handle row click in the invoice details table
  const handleInvoiceRowClick = async (invoiceData) => {
    try {
      await dispatch(fetchAdminInvoiceDetails(invoice._id));
      setSelectedInvoiceData(invoiceData);
      setShowInvoiceDetails(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  // Handle back button click from invoice details
  const handleBackFromInvoiceDetails = () => {
    setShowInvoiceDetails(false);
    setSelectedInvoiceData(null);
  };

  // If showing invoice details, render the InvoiceDetails component
  if (showInvoiceDetails) {
    return (
      <InvoiceDetails
        invoiceData={selectedInvoiceData}
        onBack={handleBackFromInvoiceDetails}
      />
    );
  }

  const handleUpdateBalance = async (values) => {
    try {
      await dispatch(attachInvoicePayment({
        invoiceId: invoice._id,
        paymentData: {
          collectedAmount: Number(values.collectedAmount),
          transactionId: values.transactionId,
          paymentDate: values.paymentDate,
          notes: values.notes
        }
      })).unwrap();
      
      showToast('Success', 'Payment attached successfully', 'success');
      setIsUpdateBalanceModalOpen(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to attach payment', 'error');
    }
  };

  const handleStatusUpdate = async (status, reason = '') => {
    try {
      setLoadingStatus(true);
      await dispatch(updateInvoiceStatus({
        invoiceId: invoice._id,
        status: selectedStatus,
        reason
      })).unwrap();
      
      await dispatch(fetchAllFundedInvoices({
        page: 1,
        limit: 0,
        searchTerm: ''
      }));
      
      showToast('Success', `Invoice status updated to ${selectedStatus} successfully`, 'success');
      setIsStatusModalOpen(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update status', 'error');
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleStatusClick = () => {
    setIsStatusModalOpen(true);
  };

  const handleAddCharges = async (values) => {
    try {
      setLoadingCharges(true);
      if (selectedCharge) {
        // Update existing charge
        await dispatch(updateInvoiceCharge({
          invoiceId: invoice._id,
          chargeId: selectedCharge.id,
          chargeData: {
            chargeAmount: Number(values.chargeAmount),
            chargeDate: values.chargeDate,
            notes: values.notes
          }
        })).unwrap();
        showToast('Success', 'Charge updated successfully', 'success');
      } else {
        // Add new charge
        await dispatch(addInvoiceCharges({
          invoiceId: invoice._id,
          chargesData: {
            chargeAmount: Number(values.chargeAmount),
            chargeId: values.chargeId,
            chargeDate: values.chargeDate,
            notes: values.notes
          }
        })).unwrap();
        showToast('Success', 'Charge added successfully', 'success');
      }
      setIsChargesModalOpen(false);
      setSelectedCharge(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to process charge', 'error');
    } finally {
      setLoadingCharges(false);
    }
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setIsUpdateBalanceModalOpen(true);
  };

  const handleDeletePayment = (payment) => {
    setSelectedPayment(payment);
    setIsDeletePaymentModalOpen(true);
  };

  const handleUpdatePayment = async (values) => {
    try {
      setLoadingPaymentAction(true);
      await dispatch(updateInvoicePayment({
        invoiceId: invoice._id,
        paymentId: selectedPayment._id,
        paymentData: {
          collectedAmount: Number(values.collectedAmount),
          transactionId: values.transactionId,
          paymentDate: values.paymentDate,
          notes: values.notes
        }
      })).unwrap();
      
      showToast('Success', 'Payment updated successfully', 'success');
      setIsUpdateBalanceModalOpen(false);
      setSelectedPayment(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update payment', 'error');
    } finally {
      setLoadingPaymentAction(false);
    }
  };

  const handleDeletePaymentConfirm = async () => {
    try {
      setLoadingPaymentAction(true);
      await dispatch(deleteInvoicePayment({
        invoiceId: invoice._id,
        paymentId: selectedPayment._id
      })).unwrap();
      
      showToast('Success', 'Payment deleted successfully', 'success');
      setIsDeletePaymentModalOpen(false);
      setSelectedPayment(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to delete payment', 'error');
    } finally {
      setLoadingPaymentAction(false);
    }
  };

  // Define charges columns inside the component
  const chargesColumns = [
    { Header: "ID", accessor: "id" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Charges Date", accessor: "date" },
    { Header: "Notes", accessor: "notes" },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <HStack spacing={2}>
          <IconButton
            icon={<FiEdit />}
            size="sm"
            variant="ghost"
            aria-label="Edit Charge"
            onClick={(e) => {
              e.stopPropagation();
              handleEditCharge(row);
            }}
          />
          <IconButton
            icon={<FiTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label="Delete Charge"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCharge(row);
            }}
          />
        </HStack>
      ),
    }
  ];

  const handleEditCharge = (charge) => {
    // Convert date from dd/mm/yyyy to mm/dd/yyyy
    const [day, month, year] = charge.date.split('/');
    const formattedDate = month + '/' + day + '/' + year;
    
    setSelectedCharge({
      ...charge,
      date: formattedDate
    });
    setIsChargesModalOpen(true);
  };

  const handleDeleteCharge = (charge) => {
    setSelectedCharge(charge);
    setIsDeleteChargeModalOpen(true);
  };

  const handleDeleteChargeConfirm = async () => {
    try {
      setLoadingChargeAction(true);
      await dispatch(deleteInvoiceCharge({
        invoiceId: invoice._id,
        chargeId: selectedCharge.id
      })).unwrap();
      
      showToast('Success', 'Charge deleted successfully', 'success');
      setIsDeleteChargeModalOpen(false);
      setSelectedCharge(null);
    } catch (error) {
      showToast('Error', error.message || 'Failed to delete charge', 'error');
    } finally {
      setLoadingChargeAction(false);
    }
  };

  const canInvoiceBeClosed = (invoice) => {
    return invoice.remainingAmount === 0;
  };

  return (
    <Box p={6}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={6}>
        <Flex align="center" gap={4}>
          <Flex align="center" gap={0}>
            <Icon
              as={FiArrowLeft}
              w={6}
              h={6}
              cursor="pointer"
              onClick={onBack}
              mr={4}
            />
            <Text fontSize="2xl" fontWeight="700">
              {invoice?.invoiceNumber || 'N/A'}
            </Text>
          </Flex>
          <Badge
            colorScheme={getStatusColor(invoice?.status)}
            bg={getStatusBgColor(invoice?.status)}
            fontSize="md"
            px={3}
            py={1}
            borderRadius="8px">
            {getStatusDisplayName(invoice?.status)}
          </Badge>
        </Flex>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            variant="outline"
            minW="150px"
            isDisabled={invoice?.status !== 'Closed' && !canInvoiceBeClosed(invoice)}>
            Status
          </MenuButton>
          <MenuList>
            {invoice?.status === 'Closed' ? (
              <MenuItem 
                onClick={() => {
                  setIsStatusModalOpen(true);
                  setSelectedStatus('Funded');
                }}
              >
                Funded
              </MenuItem>
            ) : (
              <MenuItem 
                onClick={() => {
                  setIsStatusModalOpen(true);
                  setSelectedStatus('Closed');
                }}
                isDisabled={!canInvoiceBeClosed(invoice)}
                title={!canInvoiceBeClosed(invoice) ? "Invoice must be fully paid before closing" : ""}
              >
                Close Invoice
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Flex>

      {/* Summary Cards */}
      <Flex gap={6} mb={6} flexWrap={{ base: "wrap", md: "nowrap" }}>
        <Card
          flex="2"
          p={6}
          borderRadius="12px"
          boxShadow="0px 0px 12px 0px #0000001A">
          <Text fontWeight="700" mb={4}>
            Funding Overview
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Funded Amount
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.fundedAmount}
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Remaining Balance
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.remainingBalance}
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Payment Status
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.paymentStatus}
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Debtor
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.debtor}
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Request Date
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.requestDate}
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="600" fontSize="sm">
                Due Date
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {fundingOverview.dueDate}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
        <Card
          flex="1"
          p={6}
          borderRadius="12px"
          boxShadow="0px 0px 12px 0px #0000001A">
          <Text fontWeight="700" mb={4}>
            Amount Details
          </Text>
          <Flex direction="column" gap={2}>
            <Flex justify="space-between">
              <Text color="gray.500" fontWeight="600" fontSize="sm">
                Invoice Amount
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {amountDetails.invoiceAmount}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.500" fontWeight="600" fontSize="sm">
                Funded Amount
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {amountDetails.advanceAmount}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.500" fontWeight="600" fontSize="sm">
                Escrow Amount
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {amountDetails?.escrowAmount}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.500" fontWeight="600" fontSize="sm">
                Payment Amount
              </Text>
              <Text color="gray.500" fontWeight="500" fontSize="sm">
                {amountDetails.paymentAmount}
              </Text>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      {/* Invoices Table */}
      <Card
        p={6}
        borderRadius="12px"
        boxShadow="0px 0px 12px 0px #0000001A"
        mb={6}>
        <Text fontWeight="700" mb={4}>
          Invoice Details
        </Text>
        <FundsTable
          columnsData={invoicesColumns}
          tableData={pagedInvoices}
          hideTabs={true}
          onRowClick={handleInvoiceRowClick}
        />
      </Card>

      {/* Adjust Payment & Balance Table */}
      <Card p={6} borderRadius="12px" boxShadow="0px 0px 12px 0px #0000001A">
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Flex align="center" gap={3}>
              <Text fontWeight="700" fontSize="lg">Payments & Balance</Text>
              {invoice?.remainingAmount === 0 && (
                <Badge colorScheme="green" fontSize="0.9em" px={3} py={1} borderRadius="md">
                  Fully Paid
                </Badge>
              )}
            </Flex>
            <Flex align="center" gap={2} mt={1}>
              <Text color="gray.500" fontSize="sm">
                Total Remaining: {formatCurrency(invoice?.remainingAmount || 0)}
              </Text>
            </Flex>
          </Box>
        </Flex>

        {/* Subtle info alert if fully funded */}
        {invoice?.remainingAmount === 0 && (
          <Flex
            align="center"
            bg="green.50"
            borderRadius="md"
            px={4}
            py={2}
            mt={3}
            mb={4}
            border="1px solid"
            borderColor="green.100"
          >
            <Icon as={FiDollarSign} w={5} h={5} color="green.500" mr={2} />
            <Text color="green.700" fontWeight="500" fontSize="sm">
              This invoice is fully paid. No further action is needed.
            </Text>
          </Flex>
        )}

        <Tabs variant="enclosed" colorScheme="purple" mt={6}>
          <TabList>
            <Tab _focus={{ boxShadow: "none", outline: "none" }}>Payments</Tab>
            <Tab _focus={{ boxShadow: "none", outline: "none" }}>Charges</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <Flex justify="flex-end" mb={4}>
                {invoice?.status === 'Funded' && invoice?.remainingAmount > 0 && (
                  <Button 
                    colorScheme="purple"
                    leftIcon={<Icon as={FiPlus} />}
                    onClick={() => setIsUpdateBalanceModalOpen(true)}
                  >
                    Add Payment
                  </Button>
                )}
              </Flex>

              {invoice?.payments && invoice.payments.length > 0 ? (
                <>
                  <FundsTable
                    columnsData={paymentColumns}
                    tableData={transformPaymentsData(invoice.payments)}
                    hideTabs={true}
                  />
                  <Text color="gray.500" fontSize="sm" mt={4}>
                    Total Payments: {invoice.payments.length}
                  </Text>
                </>
              ) : (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10}
                  bg="gray.50"
                  borderRadius="md"
                >
                  <Icon as={FiDollarSign} w={8} h={8} color="gray.400" mb={3} />
                  <Text color="gray.500" fontSize="md">No payments recorded yet</Text>
                  <Text color="gray.400" fontSize="sm">
                    Click "Add Payment" to record a new payment
                  </Text>
                </Flex>
              )}
            </TabPanel>

            <TabPanel px={0}>
              <Flex justify="flex-end" mb={4}>
                {invoice?.status === 'Funded' && (
                  <Button 
                    colorScheme="orange"
                    leftIcon={<Icon as={FiAlertCircle} />}
                    onClick={() => setIsChargesModalOpen(true)}
                  >
                    Add Charges
                  </Button>
                )}
              </Flex>

              {invoice?.lateCharges && invoice?.lateCharges?.length > 0 ? (
                <>
                  <FundsTable
                    columnsData={chargesColumns}
                    tableData={transformChargesData(invoice.lateCharges)}
                    hideTabs={true}
                  />
                  <Text color="gray.500" fontSize="sm" mt={4}>
                    Total Charges: {invoice?.lateCharges?.length}
                  </Text>
                </>
              ) : (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10}
                  bg="gray.50"
                  borderRadius="md"
                >
                  <Icon as={FiAlertCircle} w={8} h={8} color="gray.400" mb={3} />
                  <Text color="gray.500" fontSize="md">
                    No charges recorded yet
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    Click "Add Charges" to record new charges
                  </Text>
                </Flex>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>

      <UpdateBalanceModal
        isOpen={isUpdateBalanceModalOpen}
        onClose={() => {
          setIsUpdateBalanceModalOpen(false);
          setSelectedPayment(null);
        }}
        onSubmit={selectedPayment ? handleUpdatePayment : handleUpdateBalance}
        remainingAmount={invoice?.remainingAmount || 0}
        initialValues={selectedPayment ? {
          collectedAmount: selectedPayment.collectedAmount,
          transactionId: selectedPayment.transactionId,
          paymentDate: selectedPayment.paymentDate,
          notes: selectedPayment.notes
        } : undefined}
      />

      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setSelectedStatus('Closed');
        }}
        onConfirm={handleStatusUpdate}
        status={selectedStatus}
        loading={loadingStatus}
      />

      <ChargesModal
        isOpen={isChargesModalOpen}
        onClose={() => {
          setIsChargesModalOpen(false);
          setSelectedCharge(null);
        }}
        onSubmit={handleAddCharges}
        loading={loadingCharges}
        selectedCharge={selectedCharge}
      />

      <DeletePaymentModal
        isOpen={isDeletePaymentModalOpen}
        onClose={() => {
          setIsDeletePaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        onConfirm={handleDeletePaymentConfirm}
        payment={selectedPayment}
        loading={loadingPaymentAction}
      />

      <DeleteChargeModal
        isOpen={isDeleteChargeModalOpen}
        onClose={() => {
          setIsDeleteChargeModalOpen(false);
          setSelectedCharge(null);
        }}
        onConfirm={handleDeleteChargeConfirm}
        charge={selectedCharge}
        loading={loadingChargeAction}
      />
    </Box>
  );
};

export default FundingDetails;
